/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable react/prop-types */

import { clsx } from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { StructuredText, renderNodeRule, renderMarkRule } from 'react-datocms';
import {
	isLink,
	isParagraph,
	isRoot,
	isHeading,
} from 'datocms-structured-text-utils';
import { PBSLink } from '../../atoms/link';
import { refProp, titleProp } from '../../../types';
import { createHeadingHighlight } from '../../../utils/heading-highlight';
import AdviceFormInlineUK from '../../organisms/advise-form-inline-uk';
import { SuccessForm } from '../../organisms/success-form';
import * as headingStyles from './index.module.css';
import { buildLink, getLocale } from '../../../utils/locale';
import { useDrawer } from '../../../contexts/drawer-context';
import { sendTrackingData } from '../../../utils';
import { Button } from '../../atoms/button';

const locale = getLocale(
	typeof window !== 'undefined' ? window.location.pathname : ''
);

const MainHeaderGluAds = ({
	primaryLink,
	primaryLinkCopy,
	primaryLinkOpenForm,
	id,
	urlLocale,
	header,
	secondaryHeader,
	gluThirdHeader,
	gluFourthHeader,
	as,
	headingClassNames,
	glu,
	showInlineForm,
	note,
	ctaText,
	setReference,
	setResponse,
	reference,
	response,
	peninsulaNow,
	pageName,
	drawerRef,
	openModal,
}) => {
	const Component = as;
	const titleText = createHeadingHighlight({
		headings: header[0].children,
		headingStyles,
	});

	const { setDrawerRef } = useDrawer();

	const handleClickTracking = () => {
		const gtmStartedEvent = window.dataLayer?.find(
			(element) => element['gtm.start']
		);
		if (!gtmStartedEvent) return;

		const clickTitle = header[0].children
			.map(({ value }) => value)
			.join('');

		sendTrackingData('click', {
			event_name: 'component_interaction',
			component_name: 'main_header',
			click_type: 'primary',
			click_title: clickTitle,
			click_text: primaryLinkCopy,
		});
	};
	let cta;
	if (primaryLinkOpenForm) {
		cta = (
			<Button
				onClick={() => {
					if (
						urlLocale !== 'ca' &&
						peninsulaNow &&
						pageName === 'childService'
					) {
						openModal();
					} else {
						setDrawerRef(drawerRef);
					}
					handleClickTracking();
				}}
				className="w-max"
			>
				{primaryLinkCopy}
			</Button>
		);
	} else if (primaryLink && glu) {
		cta = (
			<PBSLink
				className="justify-center w-full text-blue-400  sm:w-max hover:text-white"
				to={primaryLink}
				variant="White"
				onClick={handleClickTracking}
			>
				{primaryLinkCopy}
			</PBSLink>
		);
	} else if (primaryLink) {
		// eslint-disable-next-line no-unused-vars
		cta = (
			<PBSLink
				className="w-max"
				to={buildLink(locale, primaryLink)}
				onClick={handleClickTracking}
			>
				{primaryLinkCopy}
			</PBSLink>
		);
	}

	return (
		<div
			className={clsx(
				'px-4  lg:py-6  lg:mx-auto lg:pr-0',
				showInlineForm ? ' mt-3' : 'mt-0',
				{
					'bg-brand-pale-400': glu,
					'': !glu,
				}
			)}
		>
			<section
				role="banner"
				className={clsx(
					'px-4  pb-6 lg:py-6 lg:mx-auto max-w-m-screen mx-auto  lg:pr-0',
					showInlineForm ? 'lg:mt-10 mt-3' : 'mt-0',
					{
						'bg-brand-pale-400 flex': glu,
						'lg:max-w-m-screen': !glu,
					}
				)}
				aria-labelledby={id}
			>
				<div>
					<div>
						<Component
							id={id}
							className={clsx(
								'!leading-[1.3] font-lexend-regular',
								!secondaryHeader ? headingStyles.heading : null,
								glu
									? 'text-2xl my-6 lg:my-0 lg:text-4xl font-bold lg:min-h-[120px]'
									: 'text-4xl md:text-5xl xl:text-6xl',
								headingClassNames
							)}
						>
							{titleText}
							{secondaryHeader ? (
								<span
									className={clsx(
										'mt-4 leading-[1.2] block',
										'md:mt-8',
										' xl:leading-[1.3]',
										glu
											? 'text-xl lg:text-2xl mb-5 font-lexend-regular'
											: `text-2xl md:text-4xl xl:text-5xl ${headingStyles.heading}`
									)}
								>
									{secondaryHeader}
								</span>
							) : null}
							{gluThirdHeader.value && (
								<StructuredText
									data={gluThirdHeader}
									customNodeRules={[
										renderNodeRule(
											isHeading,
											({ node, children, key }) => {
												const HeadingTag = `h${node.level}`;
												return (
													<HeadingTag
														className="mb-10 text-xl glu2xl:text-4xl font-lexend-regular"
														key={key}
													>
														{children}
													</HeadingTag>
												);
											}
										),
										renderNodeRule(
											isLink,
											({ node, children, key }) => (
												<PBSLink
													to={buildLink(
														locale,
														node.url
													)}
													variant="Link"
													key={key}
													className="text-black"
												>
													{children}
												</PBSLink>
											)
										),
										renderNodeRule(
											isParagraph,
											({ children, key, ancestors }) => {
												if (isRoot(ancestors[0])) {
													return (
														<p
															className="mb-10 text-lg font-notosans-light md:text-xl last-of-type:mb-0"
															key={key}
														>
															{children}
														</p>
													);
												}
												return children;
											}
										),
									]}
									customMarkRules={[
										renderMarkRule(
											'highlight',
											({ children, key }) => (
												<span
													className="font-bold text-brand-red-400"
													key={key}
												>
													{children}
												</span>
											)
										),
									]}
								/>
							)}
							{gluFourthHeader.value && (
								<StructuredText
									data={gluFourthHeader}
									customNodeRules={[
										renderNodeRule(
											isHeading,
											({ node, children, key }) => {
												const HeadingTag = `h${node.level}`;
												return (
													<HeadingTag
														className=" text-xl  font-lexend-regular"
														key={key}
													>
														{children}
													</HeadingTag>
												);
											}
										),
										renderNodeRule(
											isLink,
											({ node, children, key }) => (
												<PBSLink
													to={buildLink(
														locale,
														node.url
													)}
													variant="Link"
													key={key}
												>
													{children}
												</PBSLink>
											)
										),
										renderNodeRule(
											isParagraph,
											({ children, key, ancestors }) => {
												if (isRoot(ancestors[0])) {
													return (
														<p
															className="mb-4 text-lg font-notosans-light md:text-xl last-of-type:mb-0"
															key={key}
														>
															{children}
														</p>
													);
												}
												return children;
											}
										),
									]}
									customMarkRules={[
										renderMarkRule(
											'highlight',
											({ children, key }) => (
												<span
													className="font-bold text-brand-red-400"
													key={key}
												>
													{children}
												</span>
											)
										),
									]}
								/>
							)}
						</Component>
					</div>
					{urlLocale !== 'ca' && showInlineForm ? (
						<div
							className={clsx(
								'lg:col-span-2 2xl:col-span-5 lg:mt-0 mt-8 hidden',
								'lg:block',
								'grow',
								'shrink-0 card',
								headingStyles.imageContainer,
								reference === '' ? '' : 'w-[40%] flip'
							)}
						>
							{reference === '' ? (
								<AdviceFormInlineUK
									note={note}
									ctaText={ctaText}
									setReference={setReference}
									setResponse={setResponse}
								/>
							) : (
								<SuccessForm
									reference={reference}
									response={response}
								/>
							)}
						</div>
					) : null}
				</div>
			</section>
		</div>
	);
};

MainHeaderGluAds.defaultProps = {
	id: 'main-site-header',
	as: 'h1',
	urlLocale: '',
	secondaryHeader: '',
	headingClassNames: '',
	glu: false,
	showInlineForm: false,
	note: '',
	ctaText: 'Book a free advice call',
	setReference: () => {},
	setResponse: () => {},
	reference: '',
	response: '',
	primaryLink: '',
	primaryLinkCopy: '',
	pageName: '',
	peninsulaNow: false,
	openModal: () => {},
	primaryLinkOpenForm: false,
};

MainHeaderGluAds.propTypes = {
	id: PropTypes.string,
	urlLocale: PropTypes.string,
	header: titleProp.isRequired,
	secondaryHeader: PropTypes.string,
	as: PropTypes.string,
	headingClassNames: PropTypes.string,
	note: PropTypes.string,
	ctaText: PropTypes.string,
	primaryLink: PropTypes.string,
	primaryLinkCopy: PropTypes.string,
	primaryLinkOpenForm: PropTypes.bool,
	glu: PropTypes.bool,
	showInlineForm: PropTypes.bool,
	setReference: PropTypes.func,
	setResponse: PropTypes.func,
	reference: PropTypes.string,
	response: PropTypes.string,
	peninsulaNow: PropTypes.bool,
	pageName: PropTypes.string,
	openModal: PropTypes.func,
	// eslint-disable-next-line react/require-default-props
	drawerRef: refProp,
};

export { MainHeaderGluAds };
